import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Zap, LifeBuoy, Layers, TrendingUp } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { HOME_I18N } from "../i18n/home";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/4563ce39-d136-4158-943b-98a85fea66a9/images/0c107f763e768d30c0b3ee5f6ce696642c4455d1ca466a00b36446cf22b3f389.png";
const DASHBOARD_MOCKUP = "https://static.prod-images.emergentagent.com/jobs/4563ce39-d136-4158-943b-98a85fea66a9/images/e4e280432bf2c274958cd8cce8cc9f2b90889996c11e6f863d26e6b7cdfe661d.png";

const TRUST_ICONS = [Zap, LifeBuoy, Layers, TrendingUp];

function HeroShowcase({ t }) {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/projects").then((r) => {
      const all = r.data || [];
      const featured = all.filter((p) => p.featured);
      setItems((featured.length ? featured : all).slice(0, 8));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const timer = setInterval(() => setIdx((i) => (i + 1) % items.length), 4200);
    return () => clearInterval(timer);
  }, [items.length]);

  if (!items.length) {
    return (
      <div className="relative animate-float">
        <div className="absolute -inset-8 rounded-3xl bg-gradient-to-br from-[#2563EB]/20 via-[#22D3EE]/10 to-transparent blur-2xl" />
        <div className="relative rounded-2xl border border-white/10 bg-white/5 p-2 backdrop-blur-xl shadow-2xl">
          <img src={DASHBOARD_MOCKUP} alt={t.panelAlt} className="rounded-xl w-full" />
        </div>
      </div>
    );
  }

  const p = items[idx];

  return (
    <div className="relative animate-float" data-testid="hero-project-showcase">
      <div className="absolute -inset-8 rounded-3xl bg-gradient-to-br from-[#2563EB]/20 via-[#22D3EE]/10 to-transparent blur-2xl" />
      <div className="relative rounded-2xl border border-white/10 bg-white/5 p-2 backdrop-blur-xl shadow-2xl">
        <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#07111F]/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#22D3EE] backdrop-blur">
          <span className="h-1.5 w-1.5 rounded-full bg-[#22D3EE] animate-pulse" /> {t.liveSystems}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={p.slug}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.99 }}
            transition={{ duration: 0.6 }}
          >
            <Link to={`/projeler/${p.slug}`} data-testid="hero-project-link" className="block group">
              <div className="relative h-56 sm:h-64 overflow-hidden rounded-xl bg-[#0B1728]">
                {p.cover_image ? (
                  <img src={p.cover_image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                ) : (
                  <div className="flex h-full items-center justify-center text-white/30">{t.noImage}</div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#07111F] via-[#07111F]/30 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-5">
                  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#22D3EE]">
                    <span>{p.sector}</span>
                  </div>
                  <h3 className="mt-1.5 font-heading text-lg font-bold text-white leading-snug line-clamp-2">{p.title}</h3>
                  <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-white/90 group-hover:gap-2 transition-all">
                    {t.exploreSystem} <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        </AnimatePresence>
      </div>
      {items.length > 1 && (
        <div className="mt-4 flex justify-center gap-1.5" data-testid="hero-showcase-dots">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={t.dotAria(i + 1)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#22D3EE]" : "w-1.5 bg-white/25 hover:bg-white/40"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Hero() {
  const { lang } = useLang();
  const t = HOME_I18N[lang].hero;

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#07111F] text-white"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-30 mix-blend-screen"
        />
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="absolute inset-0 bg-hero-radial" />
        <div className="absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-[#2563EB]/30 blur-[120px] animate-orb-move" />
        <div className="absolute top-40 -right-32 h-[420px] w-[420px] rounded-full bg-[#22D3EE]/25 blur-[120px] animate-orb-move" style={{ animationDelay: "4s" }} />
        <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-[#07111F]" />
      </div>

      <div className="container-x relative pt-20 pb-20 sm:pt-24 sm:pb-28 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 lg:items-center">
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#22D3EE] backdrop-blur"
            data-testid="hero-badge"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#22D3EE] animate-pulse" />
            {t.badge}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 h1-display text-white"
            data-testid="hero-title"
          >
            {t.title1}{" "}
            <span className="text-gradient">{t.titleAccent}</span>
            {t.title2 ? ` ${t.title2}` : ""}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-white/70"
          >
            {t.desc}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.22 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <Link to="/proje-talep" data-testid="hero-cta-primary" className="btn-primary animate-glow-pulse">
              {t.ctaPrimary}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/kurumsal-cozumler" data-testid="hero-cta-secondary" className="btn-ghost-dark">
              {t.ctaSecondary}
            </Link>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.36 }}
            className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl"
            data-testid="hero-trust"
          >
            {t.trust.map((m, i) => {
              const Icon = TRUST_ICONS[i];
              return (
                <li key={m.t} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#2563EB]/30 to-[#22D3EE]/30 border border-white/10">
                    <Icon className="h-4 w-4 text-[#22D3EE]" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-white leading-snug">{m.t}</span>
                    <span className="mt-0.5 block text-xs text-white/55 leading-snug">{m.d}</span>
                  </span>
                </li>
              );
            })}
          </motion.ul>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="lg:col-span-5 relative"
          data-testid="hero-mockup"
        >
          <HeroShowcase t={t} />
        </motion.div>
      </div>
    </section>
  );
}
